import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, subscribeToAuthFailures } from './api';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'rep' | 'admin';
  avatarUrl?: string;
  currentStreak: number;
  longestStreak: number;
}

interface VerifyResetTokenResult {
  valid: boolean;
  email: string;
  name: string;
  tokenType: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  // Login link methods
  requestLoginLink: (email: string) => Promise<{ message: string }>;
  verifyLoginLink: (token: string) => Promise<void>;
  // Password reset methods
  forgotPassword: (email: string) => Promise<{ message: string }>;
  verifyResetToken: (token: string) => Promise<VerifyResetTokenResult>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async (): Promise<void> => {
    try {
      const userData = await api.get<User>('/api/auth/me');
      setUser(userData);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const data = await api.post<{ user: User }>('/api/auth/login', { email, password });
      setUser(data.user);
    } catch (err) {
      if (err instanceof ApiError) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setUser(null);
    }
  };

  const requestLoginLink = async (email: string): Promise<{ message: string }> => {
    try {
      const data = await api.post<{ message: string }>('/api/auth/login-link', { email });
      return data;
    } catch (err) {
      if (err instanceof ApiError) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const verifyLoginLink = async (token: string): Promise<void> => {
    try {
      const data = await api.post<{ user: User }>('/api/auth/login-link/verify', { token });
      setUser(data.user);
    } catch (err) {
      if (err instanceof ApiError) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const forgotPassword = async (email: string): Promise<{ message: string }> => {
    try {
      const data = await api.post<{ message: string }>('/api/auth/forgot-password', { email });
      return data;
    } catch (err) {
      if (err instanceof ApiError) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const verifyResetToken = async (token: string): Promise<VerifyResetTokenResult> => {
    try {
      const data = await api.post<VerifyResetTokenResult>('/api/auth/reset-password/verify', { token });
      return data;
    } catch (err) {
      if (err instanceof ApiError) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const resetPassword = async (token: string, password: string): Promise<void> => {
    try {
      const data = await api.post<{ user: User }>('/api/auth/reset-password', { token, password });
      setUser(data.user);
    } catch (err) {
      if (err instanceof ApiError) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => subscribeToAuthFailures(() => {
    setUser(null);
    setIsLoading(false);
  }), []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        checkAuth,
        requestLoginLink,
        verifyLoginLink,
        forgotPassword,
        verifyResetToken,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
